/**
 * VERONI collection finder — gemstone + gold picker.
 * Matches products AND the closest variant image/price/URL so the shortlist
 * actually looks like Aquamarine · Rose Gold (not a default yellow/amethyst).
 */
(function () {
  const root = document.querySelector('[data-veroni-finder]');
  if (!root) return;

  const catalogEl = root.querySelector('[data-veroni-finder-catalog]');
  const gemAssetsEl = root.querySelector('[data-veroni-finder-gem-assets]');
  let catalog = [];
  let gemAssets = {};
  try {
    catalog = JSON.parse(catalogEl?.textContent || '[]').filter((p) => p && p.url);
  } catch (e) {
    catalog = [];
  }
  try {
    gemAssets = JSON.parse(gemAssetsEl?.textContent || '{}');
  } catch (e) {
    gemAssets = {};
  }

  const type = root.dataset.collectionType || 'pieces';
  const steps = {
    intro: root.querySelector('[data-finder-step="intro"]'),
    gem: root.querySelector('[data-finder-step="gem"]'),
    gold: root.querySelector('[data-finder-step="gold"]'),
    results: root.querySelector('[data-finder-step="results"]'),
  };
  const gemMount = root.querySelector('[data-finder-gems]');
  const goldMount = root.querySelector('[data-finder-golds]');
  const picksMount = root.querySelector('[data-finder-picks]');
  const chipsMount = root.querySelector('[data-finder-chips]');
  const resultTitle = root.querySelector('[data-finder-result-title]');
  const resultSub = root.querySelector('[data-finder-result-sub]');
  const resultKicker = root.querySelector('[data-finder-result-kicker]');
  const grid = document.querySelector('[data-veroni-finder-grid]');

  const state = { gem: null, gold: null };

  const GEM_HEX = {
    ruby: '#A83246',
    emerald: '#2F8F66',
    sapphire: '#2F5389',
    amethyst: '#9B6BB0',
    'blue topaz': '#57A6C9',
    citrine: '#D99F38',
    garnet: '#8A2C3B',
    peridot: '#9BAD4F',
    diamond: '#E6E1D7',
    pearl: '#F2EDE4',
    'white topaz': '#E8E4DA',
    'green amethyst': '#7FA86F',
    'pink topaz': '#C9748E',
    'pink sapphire': '#C9748E',
    aquamarine: '#8FC6CB',
    tanzanite: '#6355A8',
    opal: '#E8DFEA',
    morganite: '#E8B4A8',
    onyx: '#2B2B2B',
    'black spinel': '#202124',
    spinel: '#6B3A5C',
    'lemon quartz': '#D6CD35',
    'rose quartz': '#D7A8AD',
    'smoky quartz': '#806B5B',
    zirconia: '#D8D4CC',
  };

  const GOLD_META = {
    'yellow gold': { className: 'v-finder__metal--yellow', hex: '#e6c78a', short: 'Yellow' },
    yellow: { className: 'v-finder__metal--yellow', hex: '#e6c78a', short: 'Yellow' },
    'white gold': { className: 'v-finder__metal--white', hex: '#e8e8e6', short: 'White' },
    white: { className: 'v-finder__metal--white', hex: '#e8e8e6', short: 'White' },
    'rose gold': { className: 'v-finder__metal--rose', hex: '#e5b7a3', short: 'Rose' },
    rose: { className: 'v-finder__metal--rose', hex: '#e5b7a3', short: 'Rose' },
  };

  const GEM_SLUGS = [
    ['green-amethyst', 'Green Amethyst'],
    ['blue-topaz', 'Blue Topaz'],
    ['white-topaz', 'White Topaz'],
    ['pink-topaz', 'Pink Topaz'],
    ['pink-sapphire', 'Pink Sapphire'],
    ['aquamarine', 'Aquamarine'],
    ['amethyst', 'Amethyst'],
    ['sapphire', 'Sapphire'],
    ['tanzanite', 'Tanzanite'],
    ['emerald', 'Emerald'],
    ['citrine', 'Citrine'],
    ['garnet', 'Garnet'],
    ['peridot', 'Peridot'],
    ['diamond', 'Diamond'],
    ['zirconia', 'Zirconia'],
    ['morganite', 'Morganite'],
    ['ruby', 'Ruby'],
    ['pearl', 'Pearl'],
    ['opal', 'Opal'],
    ['onyx', 'Onyx'],
    ['spinel', 'Spinel'],
    ['topaz', 'Topaz'],
  ];

  function normalize(s) {
    return String(s || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  function unique(list) {
    const seen = new Set();
    const out = [];
    (list || []).forEach((v) => {
      const t = String(v || '').trim();
      if (!t) return;
      const key = normalize(t);
      if (seen.has(key)) return;
      seen.add(key);
      out.push(t);
    });
    return out.sort((a, b) => a.localeCompare(b));
  }

  function optionMatches(opts, target) {
    if (!target) return true;
    const t = normalize(target);
    if (!t) return true;
    const tCore = t.replace(/\b\d+k\b/g, '').replace(/\bgold\b/g, '').trim();
    return (opts || []).some((o) => {
      const n = normalize(o);
      if (!n) return false;
      if (n === t || n.includes(t) || t.includes(n)) return true;
      const nCore = n.replace(/\b\d+k\b/g, '').replace(/\bgold\b/g, '').trim();
      return !!(tCore && nCore && (nCore === tCore || nCore.includes(tCore) || tCore.includes(nCore)));
    });
  }

  function optionsFromVariant(v) {
    const out = [];
    if (v.option1) out.push(String(v.option1));
    if (v.option2) out.push(String(v.option2));
    if (v.option3) out.push(String(v.option3));
    if (out.length) return out;
    if (Array.isArray(v.options) && v.options.length) return v.options.map(String);
    if (v.title && v.title !== 'Default Title' && v.title.indexOf('/') !== -1) {
      return v.title
        .split('/')
        .map((s) => s.trim())
        .filter(Boolean);
    }
    // Filename fallback: …-aquamarine-rose-1.webp
    // Gold must match the trailing token (…-rose-1.jpg) so names like "laura-rose-…"
    // are not misread as Rose Gold.
    const img = v.image || '';
    if (!img) return out;
    const file = img.split('/').pop().split('?')[0].toLowerCase();
    let gem = '';
    for (const [slug, label] of GEM_SLUGS) {
      if (file.includes(slug)) {
        gem = label;
        break;
      }
    }
    let gold = '';
    const goldTail = file.match(/-(yellow|white|rose)(?:-gold)?(?:-\d+)?\.(?:webp|jpe?g|png)$/);
    if (goldTail) {
      const g = goldTail[1];
      gold = g === 'yellow' ? 'Yellow Gold' : g === 'white' ? 'White Gold' : 'Rose Gold';
    }
    if (gem) out.push(gem);
    if (gold) out.push(gold);
    return out;
  }

  function scoreVariant(v, gem, gold) {
    const opts = optionsFromVariant(v);
    const gemN = normalize(gem);
    const goldN = normalize(gold);
    // Gemstone names must be exact: "Amethyst" must never match
    // "Green Amethyst", and "Topaz" must not absorb every colored topaz.
    // Variant option values and finder choices come from the same catalog
    // source, so normalized equality is both sufficient and deterministic.
    const hasGem = gemN ? opts.some((option) => normalize(option) === gemN) : false;
    const hasGold = goldN ? optionMatches(opts, goldN) : false;
    if (gemN && goldN) {
      if (hasGem && hasGold) return 120;
      if (hasGem) return 70;
      if (hasGold) return 40;
      return 0;
    }
    if (gemN) return hasGem ? 90 : 0;
    if (goldN) return hasGold ? 80 : 0;
    return 20;
  }

  function bestVariant(product, gem, gold) {
    const variants = product.variants || [];
    if (!variants.length) {
      return {
        id: null,
        image: product.image,
        price: product.price,
        options: [],
        score: gem || gold ? 0 : 20,
      };
    }
    let best = variants[0];
    let bestScore = -1;
    for (const v of variants) {
      const s = scoreVariant(v, gem, gold);
      if (s > bestScore) {
        bestScore = s;
        best = v;
      }
    }
    return {
      id: best.id,
      image: best.image || product.image,
      price: best.price || product.price,
      options: optionsFromVariant(best),
      score: Math.max(bestScore, 0),
    };
  }

  function productMatches(p) {
    // Prefer products that have a true gem+gold variant hit
    const hit = bestVariant(p, state.gem, state.gold);
    if (state.gem && state.gold) return hit.score >= 100;
    if (state.gem) return hit.score >= 70;
    if (state.gold) return hit.score >= 40;
    return true;
  }

  function allGems() {
    return unique(catalog.flatMap((p) => p.gems || []));
  }
  function allGolds() {
    return unique(catalog.flatMap((p) => p.golds || []));
  }

  function gemHex(label) {
    return GEM_HEX[normalize(label)] || '#E6E1D7';
  }

  function gemParts(label) {
    const parts = String(label || '')
      .split(/\s*&\s*/)
      .map((part) => part.trim())
      .filter(Boolean);
    return parts.length ? parts.slice(0, 3) : [String(label || '')];
  }

  function gemAsset(label) {
    let key = normalize(label);
    if (key === 'blue sapphire') key = 'sapphire';
    if (key === 'clear topaz') key = 'white topaz';
    if (/^multi(?:-|\s)?(?:stone|gemstone)$/.test(key)) key = 'multi-gemstone';
    return gemAssets[key] || '';
  }

  function gemVisualMarkup(label, compact) {
    const parts = gemParts(label);
    const segments = parts
      .map((part) => {
        const asset = gemAsset(part);
        const title = escapeAttr(part);
        if (asset) {
          return `<span class="v-finder__gemstone-segment" title="${title}"><img src="${escapeAttr(asset)}" alt="" width="48" height="48" loading="lazy" decoding="async"></span>`;
        }
        return `<span class="v-finder__gemstone-segment" title="${title}" style="background:${gemHex(part)}"></span>`;
      })
      .join('');
    return `<span class="v-finder__gemstone${compact ? ' v-finder__gemstone--compact' : ''}" data-stone-count="${parts.length}" aria-hidden="true">${segments}</span>`;
  }

  function goldMeta(label) {
    return GOLD_META[normalize(label)] || { className: 'v-finder__metal--yellow', hex: '#e6c78a', short: label };
  }

  function showStep(name) {
    Object.entries(steps).forEach(([key, el]) => {
      if (!el) return;
      const on = key === name;
      el.hidden = !on;
      el.classList.toggle('is-active', on);
      if (on) {
        el.classList.remove('is-enter');
        void el.offsetWidth;
        el.classList.add('is-enter');
      }
    });
    root.dataset.step = name;
  }

  function gemButton(label) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'v-finder__choice';
    btn.setAttribute('aria-label', label);
    btn.innerHTML = `
      ${gemVisualMarkup(label, false)}
      <span class="v-finder__choice-label">${escapeHtml(label)}</span>
    `;
    btn.addEventListener('click', () => {
      state.gem = label;
      showStep('gold');
      renderGolds();
    });
    return btn;
  }

  function goldButton(label) {
    const meta = goldMeta(label);
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'v-finder__choice v-finder__choice--metal';
    btn.innerHTML = `
      <span class="v-finder__metal ${meta.className}" style="background:${meta.hex}"></span>
      <span class="v-finder__choice-label">${escapeHtml(label)}</span>
    `;
    btn.addEventListener('click', () => {
      state.gold = label;
      finish();
    });
    return btn;
  }

  function renderGems() {
    if (!gemMount) return;
    gemMount.replaceChildren();
    const gems = allGems();
    if (!gems.length) {
      state.gem = null;
      showStep('gold');
      renderGolds();
      return;
    }
    gems.forEach((g) => gemMount.appendChild(gemButton(g)));
  }

  function renderGolds() {
    if (!goldMount) return;
    goldMount.replaceChildren();
    const golds = allGolds();
    if (!golds.length) {
      state.gold = null;
      finish();
      return;
    }
    // Prefer canonical order yellow → white → rose
    const order = ['yellow gold', 'white gold', 'rose gold'];
    golds
      .slice()
      .sort((a, b) => {
        const ia = order.indexOf(normalize(a));
        const ib = order.indexOf(normalize(b));
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      })
      .forEach((g) => goldMount.appendChild(goldButton(g)));
  }

  function renderChips() {
    if (!chipsMount) return;
    chipsMount.replaceChildren();
    const chips = [];
    if (state.gem) {
      chips.push({ label: state.gem, hex: gemHex(state.gem), gemstone: true });
    }
    if (state.gold) {
      chips.push({ label: state.gold, hex: goldMeta(state.gold).hex });
    }
    if (!chips.length) {
      chipsMount.hidden = true;
      return;
    }
    chipsMount.hidden = false;
    chips.forEach((c) => {
      const el = document.createElement('span');
      el.className = 'v-finder__chip';
      el.innerHTML = `${c.gemstone ? gemVisualMarkup(c.label, true) : `<i style="background:${c.hex}"></i>`}${escapeHtml(c.label)}`;
      chipsMount.appendChild(el);
    });
  }

  function finish() {
    const ranked = catalog
      .map((p) => {
        const hit = bestVariant(p, state.gem, state.gold);
        return { product: p, hit };
      })
      .filter(({ hit }) => {
        if (state.gem && state.gold) return hit.score >= 100;
        if (state.gem) return hit.score >= 70;
        if (state.gold) return hit.score >= 40;
        return true;
      })
      .sort((a, b) => b.hit.score - a.hit.score);

    const picks = ranked.slice(0, 6);
    const total = ranked.length;

    if (resultKicker) resultKicker.textContent = total ? 'Your edit' : 'No exact hit';
    if (resultTitle) {
      resultTitle.textContent =
        total > 0 ? (total === 1 ? '1 perfect match' : `${total} matches for you`) : 'Try another combo';
    }

    renderChips();

    if (resultSub) {
      if (total > 0) {
        const gemBit = state.gem ? state.gem : 'any stone';
        const goldBit = state.gold ? state.gold : 'any gold';
        resultSub.textContent = `Showing the closest ${type} in ${gemBit} + ${goldBit}. Tap a piece — it opens on that exact look.`;
      } else {
        resultSub.textContent = `No exact ${[state.gem, state.gold].filter(Boolean).join(' · ') || 'combo'} in the first page. Browse the full grid below.`;
      }
    }

    if (picksMount) {
      picksMount.replaceChildren();
      picks.forEach(({ product: p, hit }, i) => {
        const a = document.createElement('a');
        a.className = 'v-finder__pick';
        a.href = hit.id ? `${p.url}${p.url.includes('?') ? '&' : '?'}variant=${hit.id}` : p.url;
        a.style.setProperty('--i', String(i));
        const comboParts = [];
        if (state.gem) comboParts.push(state.gem);
        if (state.gold) comboParts.push(state.gold);
        // Prefer actual matched option labels when available
        const matchedOpts = hit.options || [];
        let comboLabel = comboParts.join(' · ');
        if (matchedOpts.length >= 2) {
          comboLabel = matchedOpts.slice(0, 2).join(' · ');
        } else if (matchedOpts.length === 1 && comboParts.length) {
          comboLabel = matchedOpts[0];
        }
        a.innerHTML = `
          <span class="v-finder__pick-media">
            ${hit.image ? `<img src="${escapeAttr(hit.image)}" alt="${escapeAttr(p.title || '')}" width="360" height="360" loading="lazy" decoding="async">` : ''}
          </span>
          <span class="v-finder__pick-meta">
            <span class="v-finder__pick-name">${escapeHtml(p.title || '')}</span>
            ${comboLabel ? `<span class="v-finder__pick-combo">${escapeHtml(comboLabel)}</span>` : ''}
            <span class="v-finder__pick-price">${escapeHtml(hit.price || p.price || '')}</span>
          </span>
        `;
        picksMount.appendChild(a);
        requestAnimationFrame(() => a.classList.add('is-in'));
      });
    }

    filterGrid();
    showStep('results');
    root.classList.add('has-results');
  }

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
  function escapeAttr(s) {
    return escapeHtml(s).replace(/'/g, '&#39;');
  }

  function filterGrid() {
    if (!grid) return;
    const cards = grid.querySelectorAll('.v-finder-card-wrap');
    let visible = 0;
    cards.forEach((card) => {
      const gems = (card.dataset.gems || '').split(',').filter(Boolean).map(normalize);
      const golds = (card.dataset.golds || '').split(',').filter(Boolean).map(normalize);
      const gemOk =
        !state.gem ||
        gems.some((g) => g === normalize(state.gem) || g.includes(normalize(state.gem))) ||
        gems.length === 0;
      const goldOk =
        !state.gold ||
        golds.some((g) => g === normalize(state.gold) || g.includes(normalize(state.gold).replace(' gold', ''))) ||
        golds.length === 0;
      const show = gemOk && goldOk;
      card.hidden = !show;
      card.classList.toggle('is-finder-match', show);
      card.classList.toggle('is-finder-dim', !show);
      if (show) visible += 1;
    });
    grid.querySelectorAll('[data-finder-editorial]').forEach((el) => {
      el.hidden = !!(state.gem || state.gold);
    });
    const note = document.querySelector('[data-veroni-grid-count]');
    if (note && (state.gem || state.gold)) {
      note.textContent = `Showing ${visible} match${visible === 1 ? '' : 'es'} for your edit`;
    }
  }

  function clearFilter() {
    state.gem = null;
    state.gold = null;
    if (chipsMount) {
      chipsMount.replaceChildren();
      chipsMount.hidden = true;
    }
    if (!grid) return;
    grid.querySelectorAll('.v-finder-card-wrap').forEach((card) => {
      card.hidden = false;
      card.classList.remove('is-finder-match', 'is-finder-dim');
    });
    grid.querySelectorAll('[data-finder-editorial]').forEach((el) => {
      el.hidden = false;
    });
    root.classList.remove('has-results');
  }

  root.querySelector('[data-finder-start]')?.addEventListener('click', () => {
    showStep('gem');
    renderGems();
  });
  root.querySelector('[data-finder-skip]')?.addEventListener('click', () => {
    clearFilter();
    document.getElementById('veroni-collection-grid')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
  root.querySelector('[data-finder-any-gem]')?.addEventListener('click', () => {
    state.gem = null;
    showStep('gold');
    renderGolds();
  });
  root.querySelector('[data-finder-any-gold]')?.addEventListener('click', () => {
    state.gold = null;
    finish();
  });
  root.querySelector('[data-finder-restart]')?.addEventListener('click', () => {
    clearFilter();
    showStep('intro');
  });
  root.querySelector('[data-finder-scroll-grid]')?.addEventListener('click', () => {
    document.getElementById('veroni-collection-grid')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  if (!catalog.length) {
    root.querySelector('[data-finder-start]')?.setAttribute('disabled', 'true');
  }
})();
